/**
 * Represents an individual action-needed item displayed in the Action Needed dialog.
 * Each item corresponds to a specific cost-review exception that requires user attention.
 */
export interface ActionNeededDto {
  /** Display label for the action-needed item */
  label: string;
  /** The type/category of the action needed (e.g., 'MarginSmBgmApproval', 'ApproverEmail', etc.) */
  type: ActionNeededType;
  /** Whether the item is visible / applicable */
  isVisible: boolean;

  // --- Info tooltip ---
  /** Whether the info icon next to the label is shown */
  isTooltipVisible?: boolean;
  /** Tooltip text displayed by the info icon */
  actionTooltip?: string;

  // --- Radio-button / approval fields ---
  /** Whether the VP/BGM approval radio-button group is shown */
  vpOrBgmApprovalVisible?: boolean;
  /** Radio-button group name used by the dialog template */
  radioButtonGroupName?: string;
  /** Current Yes/No action state (true = Yes / approved) */
  isAction?: boolean;

  // --- Textbox / combobox fields ---
  /** Bound value for textbox or combobox selections */
  actionTextValue?: string;
  /** Whether the approver-email textbox is shown */
  isApproverEmailTextboxVisible?: boolean;

  // --- Automation / Electrical / Mechanical comboboxes ---
  /** Whether the Automation reason-for-exception combobox is shown */
  isAutomationComboboxVisible?: boolean;
  /** Whether the Electrical reason-for-exception combobox is shown */
  isElectricalComboboxVisible?: boolean;
  /** Whether the Mechanical reason-for-exception combobox is shown */
  isMechanicalComboboxVisible?: boolean;

  // --- Engineer Exception fields ---
  /** Whether the Engineer Approval radio-button is shown */
  isEngineerApprovalVisible?: boolean;
  /** Current Engineer Approval state */
  isEngineerApproval?: boolean;
  /** Whether the Engineer approver-email textbox is shown */
  isEngineerApproverEmailTextboxVisible?: boolean;
  /** Whether the Engineer reason-for-exception combobox is shown */
  isEngineerReasonForExceptionComboboxVisible?: boolean;

  // --- Prefab Exception fields ---
  /** Whether the Prefab reason-for-exception combobox is shown */
  isPrefabReasonForExceptionComboboxVisible?: boolean;

  // --- Security comboboxes ---
  /** Whether the Security reason-for-exception combobox is shown */
  isSecurityComboboxVisible?: boolean;
  /** Whether the Security reason-for-labor-variance combobox is shown */
  isSecurityReasonForLaborVarianceComboboxVisible?: boolean;

  // --- Fire combobox ---
  /** Whether the Fire reason-for-exception combobox is shown */
  isFireComboboxVisible?: boolean;
  isFireJobDetailsVisible?: boolean;

  // --- SM Approval (TBE / Fire) ---
  /** Whether the SM Approval radio-button is shown */
  isSMApprovalVisible?: boolean;
  /** Current SM Approval state */
  isSMApproval?: boolean;
}

/**
 * Enum identifying the specific kind of action-needed entry.
 */
export enum ActionNeededType {
  // Automation / Electrical / Mechanical
  MarginSmBgmApproval = 'MarginSmBgmApproval',
  MarginApproverEmail = 'MarginApproverEmail',
  MarginReasonForException = 'MarginReasonForException',
  EscalationSmBgmApproval = 'EscalationSmBgmApproval',
  EngineerApproval = 'EngineerApproval',
  EngineerApproverEmail = 'EngineerApproverEmail',
  EngineerReasonForException = 'EngineerReasonForException',
  PrefabReasonForException = 'PrefabReasonForException',
  // Fire
  FireZteApproval = 'FireZteApproval',
  FireTbeSmApproval = 'FireTbeSmApproval',
  fireJobDetails = 'fireJobDetails',
  // Security
  SecurityMarginSmBgmApproval = 'SecurityMarginSmBgmApproval',
  SecurityMarginApproverEmail = 'SecurityMarginApproverEmail',
  SecurityMarginReasonForException = 'SecurityMarginReasonForException',
  SecurityEscalationSmBgmApproval = 'SecurityEscalationSmBgmApproval',
  SecurityReasonForLaborVariance = 'SecurityReasonForLaborVariance',
}
